import { useState } from "react";
import { Phone, Mail, MapPin, Navigation, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import api from "../lib/api";
import { BUSINESS } from "../lib/site";
import { OpeningHours } from "../components/layout/Footer";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const MAPS_QUERY = encodeURIComponent("Margaret House, Huyton Road, Adlington, PR7 4HD");

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", { ...form, type: "general" });
      toast.success("Message sent. We will get back to you shortly.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error("Could not send your message. Please call us instead.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact PR7 MOT Centre | Margaret House, Huyton Road, Adlington PR7 4HD"
        description="Get in touch with PR7 MOT Centre, Adlington. Call 01257 786 540, email bookings@pr7motcentre.co.uk or visit Margaret House, Huyton Road, Adlington, PR7 4HD."
        path="/contact"
      />

      <section className="relative bg-navy overflow-hidden">
        <div className="absolute inset-0 eng-grid" />
        <div className="container-pr7 relative py-16 lg:py-20">
          <p className="eyebrow-light mb-3">Contact</p>
          <h1 className="font-heading font-black text-white text-4xl sm:text-5xl tracking-tight">Get in touch</h1>
        </div>
      </section>

      <section className="section-pad bg-surface">
        <div className="container-pr7 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-4">
            <a href={BUSINESS.phoneHref} data-testid="contact-phone-card" className="flex items-center gap-4 bg-white border border-border rounded-lg p-6 hover:border-brand/40 hover:shadow-md transition-all group">
              <span className="bg-pale text-brand p-3 rounded-md group-hover:bg-brand group-hover:text-white transition-colors"><Phone size={22} /></span>
              <span>
                <span className="block text-xs font-heading font-bold uppercase tracking-wider text-muted-foreground">Call us</span>
                <span className="block font-heading font-extrabold text-ink text-xl mt-0.5">{BUSINESS.phone}</span>
              </span>
            </a>
            <a href={`mailto:${BUSINESS.email}`} data-testid="contact-email-card" className="flex items-center gap-4 bg-white border border-border rounded-lg p-6 hover:border-brand/40 hover:shadow-md transition-all group">
              <span className="bg-pale text-brand p-3 rounded-md group-hover:bg-brand group-hover:text-white transition-colors"><Mail size={22} /></span>
              <span className="min-w-0">
                <span className="block text-xs font-heading font-bold uppercase tracking-wider text-muted-foreground">Email us</span>
                <span className="block font-heading font-bold text-ink text-base mt-0.5 break-all">{BUSINESS.email}</span>
              </span>
            </a>
            <div className="bg-white border border-border rounded-lg p-6">
              <div className="flex items-start gap-4">
                <span className="bg-pale text-brand p-3 rounded-md shrink-0"><MapPin size={22} /></span>
                <div>
                  <p className="text-xs font-heading font-bold uppercase tracking-wider text-muted-foreground">Visit us</p>
                  <p className="font-heading font-bold text-ink text-base mt-1 leading-relaxed" data-testid="contact-address">{BUSINESS.address}</p>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${MAPS_QUERY}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="directions-btn"
                    className="mt-3 inline-flex items-center gap-2 text-sm font-heading font-bold text-brand hover:text-sky tracking-wide transition-colors"
                  >
                    <Navigation size={14} /> GET DIRECTIONS
                  </a>
                </div>
              </div>
            </div>
            <div className="bg-navy rounded-lg p-6 relative overflow-hidden">
              <div className="absolute inset-0 eng-grid" />
              <div className="relative">
                <p className="font-heading font-bold text-white text-sm uppercase tracking-wider mb-3">Opening Hours</p>
                <OpeningHours dark />
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-6">
            <div className="bg-white border border-border rounded-lg p-7">
              <h2 className="font-heading font-extrabold text-ink text-2xl tracking-tight">Send us a message</h2>
              <form data-testid="contact-form" onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
                <div>
                  <Label htmlFor="ct-name" className="text-xs font-heading font-bold uppercase tracking-wider text-ink/70">Name *</Label>
                  <Input id="ct-name" data-testid="contact-name" required value={form.name} onChange={set("name")} className="bg-white h-11" />
                </div>
                <div>
                  <Label htmlFor="ct-phone" className="text-xs font-heading font-bold uppercase tracking-wider text-ink/70">Phone</Label>
                  <Input id="ct-phone" data-testid="contact-phone" type="tel" value={form.phone} onChange={set("phone")} className="bg-white h-11" />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="ct-email" className="text-xs font-heading font-bold uppercase tracking-wider text-ink/70">Email</Label>
                  <Input id="ct-email" data-testid="contact-email" type="email" value={form.email} onChange={set("email")} className="bg-white h-11" />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="ct-msg" className="text-xs font-heading font-bold uppercase tracking-wider text-ink/70">Message *</Label>
                  <Textarea id="ct-msg" data-testid="contact-message" required rows={5} value={form.message} onChange={set("message")} className="bg-white" placeholder="How can we help?" />
                </div>
                <div className="sm:col-span-2">
                  <button type="submit" data-testid="contact-submit-btn" disabled={busy} className="btn-primary disabled:opacity-60">
                    {busy ? "SENDING..." : "SEND MESSAGE"} <ArrowRight size={16} />
                  </button>
                </div>
              </form>
            </div>

            <div className="bg-white border border-border rounded-lg overflow-hidden">
              <iframe
                title="PR7 MOT Centre location map"
                data-testid="contact-map"
                src={`https://www.google.com/maps?q=${MAPS_QUERY}&output=embed`}
                className="w-full h-80 border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
